/**
 * Peças da "Sua primeira busca" que o guia passo a passo também usa (28/10/2026): as palavras de
 * lugar, o resumo do perfil e o preenchimento do formulário de captura a partir de uma sugestão.
 * Saíram do FirstSearchDialog sem mudar o comportamento.
 */
import { saveCaptureForm } from '../../lib/captureFormMemory';
import { fetchLocations } from '../../lib/locationSearch';
import { flagEmoji } from '../../lib/format';
import { localeOf } from '../home/homeText';
import { norm, type ReachId } from './catalog';
import { cityLabel, type UserPlace } from './place';
import {
  SUGGESTIONS,
  pickSuggestions,
  suggestionWhere,
  type PlaceWords,
  type SuggestionDef,
  type SuggestionForm,
  type SuggestionId,
} from './suggestions';
import type { OnboardingKey } from './onboardingText';
import type { OnboardingResult } from './OnboardingDialog';

export type Tx = (key: OnboardingKey, vars?: Record<string, string | number>) => string;
type SuggestionText = (id: SuggestionId, field: 'term' | 'kw' | 'why') => string;

export interface SugestaoMontada {
  id: SuggestionId;
  def: SuggestionDef;
  term: string;
  why: string;
  where: string;
  form: SuggestionForm;
}

export function capitalize(text: string): string {
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : text;
}

export function placeWords(tx: Tx, lang: string): PlaceWords {
  const brazilPt = (p: UserPlace): boolean => lang === 'pt' && p.country === 'BR';
  return {
    inCity: (city) => tx('in_city', { city }),
    inRegion: (city) => tx('in_region', { city }),
    inState: (p) =>
      p.state && lang === 'pt'
        ? p.state.inPt
        : tx('in_state', { state: p.state?.name ?? p.region ?? '' }),
    inCountry: (p) => (brazilPt(p) ? tx('in_brazil') : tx('in_country')),
    region: (city) => tx('region', { city }),
    country: (p) => (brazilPt(p) ? tx('brazil') : tx('country')),
    allAreaCodes: tx('all_ddd'),
    areaCode: (ddd) => tx('ddd', { ddd }),
  };
}

/** "em Curitiba/PR", "no Paraná", "em todo o Brasil", "em qualquer lugar" — para o resumo. */
export function reachWords(reach: ReachId, place: UserPlace, words: PlaceWords, tx: Tx): string {
  const city = cityLabel(place);
  if (reach === 'cidade' && city) return words.inCity(city);
  if (reach === 'regiao' && city) return words.inRegion(city);
  if (reach === 'estado' && (place.state || place.region)) return words.inState(place);
  if (reach === 'online') return tx('anywhere');
  if (reach === 'pais') return words.inCountry(place);
  return '';
}

/** "Clínica odontológica que vende para empresas em Curitiba/PR e região". */
export function profileSummary(
  profile: OnboardingResult,
  place: UserPlace,
  words: PlaceWords,
  tx: Tx,
): string {
  return tx('fs_profile', {
    segment: capitalize(profile.segmentLabel || tx('fs_your_business')),
    customers: tx(`cw_${profile.customer}` as OnboardingKey),
    where: reachWords(profile.reach, place, words, tx),
  }).replace(/\s+$/, '');
}

/** As 3 buscas do perfil, com o lugar e o formulário prontos. */
export function buildSuggestions(
  profile: OnboardingResult,
  place: UserPlace,
  words: PlaceWords,
  suggestion: SuggestionText,
): SugestaoMontada[] {
  const ids = pickSuggestions({
    sectorId: profile.sectorId,
    segmentId: profile.segmentId,
    customer: profile.customer,
  });
  return ids.map((id) => {
    const def = SUGGESTIONS[id]!;
    const kw = suggestion(id, 'kw');
    const { where, form } = suggestionWhere(def, kw, profile.reach, place, words);
    return { id, def, term: suggestion(id, 'term'), why: suggestion(id, 'why'), where, form };
  });
}

/** Grava o formulário do módulo como se a pessoa tivesse preenchido. */
export async function fillCaptureForm(
  form: SuggestionForm,
  place: UserPlace,
  lang: string,
): Promise<void> {
  if (form.module === 'places') {
    const tag = form.city ? await cityTag(place, lang) : null;
    saveCaptureForm('places', {
      keywords: form.keywords,
      places: tag ? [tag] : [],
      ...(form.distance ? { distance: form.distance } : {}),
    });
    return;
  }
  if (form.module === 'phones') {
    saveCaptureForm('phones', {
      keywords: form.keywords,
      sites: form.sites,
      country: form.country
        ? { value: form.country, text: countryText(form.country, lang) }
        : undefined,
      areaCode: form.areaCode ?? null,
    });
    return;
  }
  saveCaptureForm('emails', { keywords: form.keywords, sites: form.sites, domains: form.domains });
}

function countryText(iso: string, lang: string): string {
  let name = iso.toUpperCase();
  try {
    name =
      new Intl.DisplayNames([localeOf(lang)], { type: 'region' }).of(iso.toUpperCase()) ?? name;
  } catch {
    /* Intl.DisplayNames indisponível */
  }
  const flag = flagEmoji(iso);
  return flag ? `${flag} ${name}` : name;
}

/** Tag `lat,lng|nome` da cidade, pelo typeahead do formulário (ou o ponto do IP). */
async function cityTag(place: UserPlace, lang: string): Promise<string | null> {
  if (!place.city) return null;
  const query = [place.city, place.state?.name ?? place.region].filter(Boolean).join(', ');
  try {
    const found = await Promise.race([
      fetchLocations(query, lang),
      new Promise<[]>((resolve) => setTimeout(() => resolve([]), 4000)),
    ]);
    const city = norm(place.city);
    const best = found.find((p) => norm(p.short).startsWith(city)) ?? found[0];
    if (best) return `${best.geo}|${best.short}`;
  } catch {
    /* typeahead fora do ar */
  }
  return place.geo ? `${place.geo}|${place.city}` : null;
}
